import { LoadingButton } from '@mui/lab'
import {
  Box,
  buttonClasses,
  Card,
  CardHeader,
  Divider,
  Paper,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import { ArrowRight, Sparkle } from '@phosphor-icons/react'

import { BASE_MAP_STYLE_URL } from 'map-style-utils'

import 'maplibre-gl/dist/maplibre-gl.css'

import { ellipsis } from 'polished'
import { useState } from 'react'
import MapGl from 'react-map-gl/maplibre'

import { BrandLogo } from 'ui-components/components/atoms/BrandLogo'
import { MapFileInput } from 'ui-components/components/molecules/MapFileInput'
import { IntegrationComingSoon } from './MapIntegrations'

export function CobuilderMapFileUpload(props) {
  const { slots } = props

  const Slots = {
    Footer,
    MapFileInput,
    ...slots
  }

  const [integration, setIntegration] = useState('')
  const [showInput, setShowInput] = useState(false)

  return (
    <>
      <Head heading='Get up and running fast!' />

      <Body>
        <MapFileInputForm>
          {!showInput && (
            <slots.MapIntegrations
              onSupportIntegrationClick={() => {
                setShowInput(true)
              }}
              onIntegrationClick={(integration) => {
                setShowInput(true)
                setIntegration(integration)
              }}
            />
          )}
          {showInput && integration.length > 0 && (
            <IntegrationComingSoon integration={integration} />
          )}
          {showInput && <Slots.MapFileInput />}
        </MapFileInputForm>
      </Body>

      <Slots.Footer />
      <slots.NoDataOption />
    </>
  )
}

export function Layout(props) {
  return (
    <Box
      key='CobuilderLayout'
      sx={[
        {
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.primary.light,
          display: 'flex',
          flexDirection: 'row',
          p: 8,
          gap: 8,
          '> :first-child': {
            flex: 1,
            flexBasis: '240px'
          },
          '> :nth-child(2)': {
            flex: 2,
            flexBasis: '600px'
          }
        },
        (theme) => ({
          [theme.breakpoints.down(1200)]: {
            flexDirection: 'column'
          }
        })
      ]}
      {...props}
    />
  )
}

export function Left(props) {
  const { children, ...rest } = props
  return (
    <Stack
      key='CobuilderLeft'
      spacing={4}
      {...rest}
    >
      {children}
    </Stack>
  )
}

export function Right(props) {
  const { children, ...rest } = props
  return (
    <Stack
      key='CobuilderRight'
      sx={{ position: 'relative' }}
      {...rest}
    >
      {children}
    </Stack>
  )
}

export function Head(props) {
  return (
    <Stack spacing={4}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
      >
        <SvgIcon
          component={Sparkle}
          weight='duotone'
          inheritViewBox
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 32,
            height: 32
          }}
        />
        <Typography
          variant='next:subtitle1'
          letterSpacing='tight'
          color='primary'
          fontWeight={600}
          pt={1}
        >
          Mapstack Cobuilder
        </Typography>
      </Stack>

      {props.heading && (
        <Typography
          variant='next:h4'
          fontWeight={700}
          letterSpacing='tight'
        >
          {props.heading}
        </Typography>
      )}

      {props.subheading && (
        <Typography
          variant='next:h6'
          letterSpacing='tight'
        >
          {props.subheading}
        </Typography>
      )}
    </Stack>
  )
}

export function Body(props) {
  return (
    <Stack
      {...props}
      spacing={4}
    />
  )
}

export function Footer(props) {
  const { slotProps } = props

  return (
    <Stack
      gap={2}
      width='100%'
      direction='row'
      flexWrap='wrap'
      justifyContent='flex-end'
      sx={(theme) => ({
        [theme.breakpoints.down(780)]: {
          flexDirection: 'column-reverse',
          [`.${buttonClasses.root}`]: {
            flex: 1
          }
        }
      })}
    >
      <LoadingButton
        variant='contained'
        color='primary'
        loadingPosition='start'
        sx={[
          slotProps?.ContinueButton?.loading && {
            pl: 5
          }
        ]}
        {...slotProps?.ContinueButton}
      >
        {slotProps?.ContinueButton?.loading
          ? 'Processing'
          : slotProps?.ContinueButton?.text || 'Continue'}
      </LoadingButton>
    </Stack>
  )
}

function MapFileInputForm(props) {
  return (
    <Stack spacing={4}>
      <Typography
        variant='next:subtitle1'
        letterSpacing='tight'
      >
        Import your address or coordinate data to unlock amazing map based
        insights.
      </Typography>
      {props.children}
    </Stack>
  )
}

export function Map(props) {
  const { slots, children, showDataModal } = props

  const Slots = {
    MapTitle: MapName,
    MapCanvas,
    ...slots
  }

  return (
    <Paper
      key='CobuilderMap'
      className='Map'
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 2
      }}
      elevation={8}
    >
      <Stack
        direction='column'
        height='100%'
        overflow='hidden'
        borderRadius={2}
      >
        <CardHeader
          sx={{ p: 2 }}
          title={
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              width='fit-content'
            >
              <BrandLogo />
              <Slots.MapName />
            </Stack>
          }
          titleTypographyProps={{
            variant: 'next:h6',
            fontWeight: 600,
            letterSpacing: 'tight'
          }}
        />
        <Divider />
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {showDataModal && <MapDataModal />}
          <Slots.MapCanvas />
          {children}
        </Box>
      </Stack>
    </Paper>
  )
}

export function MapName(props) {
  return (
    <Box
      className='MapTitle'
      component='div'
      sx={[{ width: '100%', minWidth: 100, maxWidth: 100 }]}
      title={props.name}
    >
      <Box
        component='div'
        sx={[ellipsis('80ch')]}
      >
        {props.name || 'New map'}
      </Box>
    </Box>
  )
}

export function MapCanvas() {
  return (
    <MapGl
      reuseMaps
      style={{
        width: '100%',
        height: '100%'
      }}
      mapStyle={BASE_MAP_STYLE_URL}
    />
  )
}

const MapDataModal = () => {
  return (
    <Paper
      variant='elevation'
      elevation={8}
      sx={{
        borderRadius: 2,
        padding: 2,
        position: 'absolute',
        zIndex: 999,
        display: 'flex',
        margin: 'auto',
        backgroundColor: 'warning.main'
      }}
    >
      <Typography
        variant='body1'
        color='white'
      >
        See your data on this map!
      </Typography>
    </Paper>
  )
}
export const NoDataOption = ({ handleDemoMapClick }) => {
  return (
    <Stack
      spacing={2}
      direction='column'
      sx={{
        alignItems: 'center'
      }}
    >
      <Typography color='text.secondary'>— or just exploring ? —</Typography>
      <Card sx={{ width: '100%' }}>
        <Stack
          direction='column'
          spacing={1}
          sx={{ alignItems: 'center', p: 2 }}
        >
          <Typography variant='body1'>
            👀 Try our interactive demo map
          </Typography>
          <Typography
            color='primary'
            component='a'
            variant='subtitle2'
            onClick={handleDemoMapClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            📍 Visualize sales distribution across the UK
            <ArrowRight
              size={16}
              color='currentColor'
            />
          </Typography>
        </Stack>
      </Card>
      <Card sx={{ width: '100%' }}>
        <Stack
          direction='column'
          spacing={1}
          sx={{ alignItems: 'center', p: 2 }}
        >
          <Typography variant='body1'>🧪 Want to test yourself?</Typography>
          <Typography
            color='primary'
            component='a'
            href='/sampleData/Acme%20Coffee%20Sales%20-%20Sample%20Data.xlsx'
            download
            variant='subtitle2'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none'
            }}
          >
            ⬇️ Download sample spreadsheet
          </Typography>
        </Stack>
      </Card>
      <Typography
        color='text.secondary'
        fontSize='small'
      >
        ✨ See powerful insights without uploading your own data.
      </Typography>
    </Stack>
  )
}
